export type ServicePriceType = 'fixed' | 'starting' | 'contact' | 'call' | 'add-on' | 'per-session'

export type ServiceItem = {
  name: string
  price: string
  priceType: ServicePriceType
  note?: string
}

export type ServiceCategory = {
  title: string
  number: string
  description: string
  items: ServiceItem[]
  optionsLabel?: string
  options?: string[]
  layout?: 'standard' | 'wide' | 'compact'
}

export type ServiceMenu = {
  eyebrow: string
  title: string
  introduction: string
  pricingNote?: string
  categories: ServiceCategory[]
}

export const serviceMenus: Record<'salon' | 'spa', ServiceMenu> = {
  salon: {
    eyebrow: 'Hair Services',
    title: 'Fresh shape, dimensional color, healthy shine.',
    introduction: 'Thoughtful hair services tailored to your goals, maintenance preferences, and consultation with your stylist.',
    pricingNote: 'All prices are starting prices and may vary based on the stylist, hair length, density, and the service plan created during your consultation.',
    categories: [
      {
        title: 'Cuts',
        number: '01',
        description: 'Personalized cuts shaped around your hair, routine, and preferred look.',
        items: [
          { name: 'Women’s Cut', price: 'Starting at $65+', priceType: 'starting' },
          { name: 'Men’s Cut', price: 'Starting at $55+', priceType: 'starting' },
        ],
      },
      {
        title: 'Color',
        number: '02',
        description: 'Color maintenance and all-over tone planned with your stylist.',
        items: [
          { name: 'Base Touch-Up', price: 'Starting at $90+', priceType: 'starting' },
          { name: 'All-Over Color', price: 'Starting at $130+', priceType: 'starting' },
        ],
      },
      {
        title: 'Highlights and Dimensional Color',
        number: '03',
        description: 'Customized lightening and dimensional color based on your starting point and goals.',
        items: [
          { name: 'Partial Highlight', price: 'Starting at $190+', priceType: 'starting' },
          { name: 'Full Highlight', price: 'Starting at $210+', priceType: 'starting' },
          { name: 'Partial Balayage / Foilyage', price: 'Contact for pricing', priceType: 'contact' },
          { name: 'Full Balayage / Foilyage', price: 'Contact for pricing', priceType: 'contact' },
        ],
      },
      {
        title: 'Styling and Treatments',
        number: '04',
        description: 'Finishing, conditioning, smoothing, and extension services for your desired look and maintenance.',
        items: [
          { name: 'Blowout', price: 'Starting at $60+', priceType: 'starting' },
          { name: 'Gloss', price: 'Starting at $40+', priceType: 'starting' },
          { name: 'Conditioning Treatment', price: 'Starting at $40+', priceType: 'starting' },
          { name: 'Smoothing / Keratin Treatment', price: 'Starting at $200+', priceType: 'starting' },
          { name: 'Extensions', price: 'Call for information', priceType: 'call' },
        ],
      },
    ],
  },
  spa: {
    eyebrow: 'Spa Services',
    title: 'Thoughtful care for skin, body, brows, and lashes.',
    introduction: 'Explore restorative treatments and finishing services offered by Diamond Salon’s spa professionals.',
    categories: [
      {
        title: 'Custom Facials',
        number: '01',
        description: 'Personalized facial care selected with your skincare professional.',
        items: [
          { name: '45-Minute Custom Facial', price: '$75', priceType: 'fixed' },
          { name: '90-Minute Custom Facial', price: '$150', priceType: 'fixed' },
        ],
        optionsLabel: 'Available facial options include',
        options: ['Anti-Aging', 'Hydrating', 'Acne', 'Oxygen', 'Enzymatic / Chemical Peels', 'Back Facials', 'LED Light Treatment'],
      },
      {
        title: 'Treatments',
        number: '02',
        description: 'Targeted spa and beauty treatments, with timing noted where applicable.',
        items: [
          { name: 'Hot Stone Massage, 75 Minutes', price: '$120', priceType: 'fixed' },
          { name: 'Eyelash Tint and Treatment', price: '$125', priceType: 'fixed' },
          { name: 'LED Treatment, 30 Minutes', price: '$35', priceType: 'fixed' },
          { name: 'Dermaplaning With Facial', price: '$50 add-on', priceType: 'add-on' },
          { name: 'Dermaplaning Without Facial', price: '$75', priceType: 'fixed' },
        ],
      },
      {
        title: 'Waxing Services',
        number: '03',
        description: 'Face and body waxing services, with starting prices shown where the service may vary.',
        layout: 'wide',
        items: [
          { name: 'Brows', price: '$30', priceType: 'fixed' },
          { name: 'Bikini', price: 'Starting at $40+', priceType: 'starting' },
          { name: 'Lip, Chin, or Cheeks', price: '$20', priceType: 'fixed' },
          { name: 'Half Legs', price: '$40', priceType: 'fixed' },
          { name: 'Full Legs', price: '$75', priceType: 'fixed' },
          { name: 'Half Arms', price: '$25', priceType: 'fixed' },
          { name: 'Full Arms', price: '$50', priceType: 'fixed' },
          { name: 'Chest or Back', price: 'Starting at $75+', priceType: 'starting' },
        ],
      },
      {
        title: 'Additional Spa Services',
        number: '04',
        description: 'Specialized services available through Diamond Salon’s spa professionals.',
        layout: 'compact',
        items: [
          { name: 'Luxury Eyelash Treatment and Lift', price: 'Starting at $125+', priceType: 'starting' },
          { name: 'ProCell Microchanneling', price: '$360 per session', priceType: 'per-session' },
        ],
      },
    ],
  },
}

export const serviceAvailabilityNote = 'Service availability and final pricing may vary by professional. Contact Diamond Salon or book a consultation to confirm availability and receive personalized pricing.'
